import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { selectCompany } from "../../homeSlice";
import { useNavigate, useParams } from "react-router-dom";
import { useCallback, useEffect, useMemo } from "react";
import {
  planningsAsync,
  selectPlanningsByDayStatus,
  selectTripsByDayStatus,
  tripsAsync,
} from "../trips/tripsSlice";
import {
  selectAdvancedAnalyticsVehiclesList,
  selectAdvancedAnalyticsVehiclesListStatus,
} from "../fleetSlice";
import "../../../../i18n/i18n";
import { SharedInterfaceHistory } from "./sharedInterface";
import {
  authorizationsRejectedByCompanyByPeriodListAsync,
  selectAuthorizationsListStatus,
  transactionsListByPeriodAsync,
} from "../../transactions/transactionsSlice";
import { HistoryTripsSidePanel } from "./sidePanel/sidePanel";
import { TripsAndPlanningsHistory } from "../trips/tripsApi";

export const HistoryTab = () => {
  const company = useAppSelector(selectCompany);
  const params = useParams();
  const navigate = useNavigate();
  const firstPath = location.pathname.split("/")[1]; 
  const dispatch = useAppDispatch();
  const advancedAnalyticsVehicleList = useAppSelector(
    selectAdvancedAnalyticsVehiclesList,
  );
  const advancedAnalyticsVehicleListStatus = useAppSelector(
    selectAdvancedAnalyticsVehiclesListStatus,
  );
  const tripsListStatus = useAppSelector(selectTripsByDayStatus);
  const planningsListStatus = useAppSelector(selectPlanningsByDayStatus);
  const authorizationsListStatus = useAppSelector(
    selectAuthorizationsListStatus,
  );

  const loadAuthorizationsRejectedByPeriodAction = useCallback(
    (payload: { from: string; to: string }) => {
      if (!company) {
        return;
      }
      void dispatch(
        authorizationsRejectedByCompanyByPeriodListAsync({
          companyUuid: company.uuid,
          from: payload.from,
          to: payload.to,
        }),
      );
    },
    [company, dispatch],
  );

  const loadTransactionByPeriodAction = useCallback(
    (payload: { from: string; to: string }) => {
      if (!company) {
        return;
      }
      void dispatch(
        transactionsListByPeriodAsync({
          companyUuid: company.uuid,
          from: payload.from,
          to: payload.to,
        }),
      );
    },
    [company, dispatch],
  );

  useEffect(() => {
    if (
      company &&
      company.hasAdvancedAnalytics &&
      advancedAnalyticsVehicleList &&
      advancedAnalyticsVehicleList.length > 0 &&
      advancedAnalyticsVehicleListStatus === "success"
    ) {
      if (tripsListStatus === "idle")
        void dispatch(
          tripsAsync({
            vehicles: advancedAnalyticsVehicleList,
          }),
        );
      if (planningsListStatus === "idle")
        void dispatch(planningsAsync({ companyUuid: company.uuid }));
    }
  }, [
    dispatch,
    advancedAnalyticsVehicleListStatus,
    advancedAnalyticsVehicleList,
    tripsListStatus,
    company,
    planningsListStatus,
    authorizationsListStatus,
  ]);

  const hasNoAdvancedAnalyticsVehicles = useMemo(() => {
    return (
      advancedAnalyticsVehicleList.length === 0 &&
      advancedAnalyticsVehicleListStatus === "success"
    );
  }, [advancedAnalyticsVehicleList.length, advancedAnalyticsVehicleListStatus]);

  const sidePanelAction = useCallback(
    (props: { currentTripId: string }) => {
      navigate(`/${firstPath}/fleet/history/${props.currentTripId}`);
    },
    [navigate, firstPath],
  );

  if (params.tab !== "history") {
    return;
  }

  return (
    <SharedInterfaceHistory
      hasNoAdvancedAnalyticsVehicles={hasNoAdvancedAnalyticsVehicles}
      loadTransactionByPeriodAction={loadTransactionByPeriodAction}
      loadAuthorizationsRejectedByPeriodAction={
        loadAuthorizationsRejectedByPeriodAction
      }
      sidePanelAction={sidePanelAction}
      sidePanel={(props: { trip: TripsAndPlanningsHistory; previousTripId: string | null; nextTripId: string | null }) => 
        <HistoryTripsSidePanel 
          tripsAndPlanningsHistory={props.trip}
          previousTripId={props.previousTripId}
          nextTripId={props.nextTripId}
        />
      }
    />
  );
};
